import { produce } from "immer";
import * as messageActions from "pages/Mail/Messages/store/messageActions";
import * as conversationActions from "pages/Mail/ReadMessage/store/conversationActions";
import * as singleMessageActions from "pages/Mail/ReadMessage/store/singleMessageActions";
import * as folderActions from "pages/Folders/store/folderActions";
import * as logsActions from "pages/Logs/store/logsActions";
import * as activeDevicesActions from "pages/Settings/ActiveDevices/store/activeDevicesActions";
import * as failedSessionsActions from "pages/Settings/ActiveDevices/store/failedSessionsActions";
import * as quarantinesActions from "pages/Quarantine/store/quarantinesActions";
import * as singleQuarantineActions from "pages/Quarantine/store/singleQuarantineActions";
import * as archivesActions from "pages/Archive/store/archivesActions";
import * as singleArchiveActions from "pages/Archive/store/singleArchiveActions";
import * as savedArchiveSearchesActions from "pages/Archive/SavedSearches/store/savedSearchesActions";
import * as rulesActions from "pages/Settings/Rules/store/rulesActions";

import * as uiActions from "store/actions/uiActions";

export const ENUMS = {
  SUCCESS: "success",
  FETCHING: "fetching",
  PAGE_FETCHING: "pageFetching",
  FAILED: "failed",
  SILENT: "silent"
};

const initialState = {
  isLoading: false,
  root: {
    byId: {}
  },
  folders: {
    byId: {}
  },
  subFolders: {
    byId: {}
  },
  messages: {
    byId: {}
  },
  logs: {
    byId: {}
  },
  activeDevices: {
    byId: {}
  },
  failedSessions: {
    byId: {}
  },
  quarantines: {
    byId: { list: { message: ENUMS.SILENT } }
  },
  archives: {
    byId: {}
  },
  savedArchiveSearches: {
    byId: {}
  },
  conversations: {
    byId: {}
  },
  conversationsMessages: {
    byId: {}
  },
  rules: {
    byId: {}
  }
};

const reducer = produce((draft, action) => {
  switch (action.type) {
    case uiActions.SET_LOADING: {
      const payload = action.payload;
      draft.isLoading = payload.isLoading;

      break;
    }

    case uiActions.SET_DELETING_MESSAGES_IDS: {
      const { payload } = action;
      draft.deletingMessagesIds = payload.ids;

      break;
    }

    case messageActions.FETCH_MESSAGES: {
      const payload = action.payload;
      // silent mode for silent message fetching without loading indicator
      const status = payload.silent ? ENUMS.SILENT : ENUMS.FETCHING;

      draft.folders.byId[payload.folder] = {
        message: status,
        appendMessage: null
      };

      break;
    }

    case messageActions.FETCH_MESSAGES_FAILED: {
      const payload = action.payload;
      draft.folders.byId[payload.folder] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };

      break;
    }

    case messageActions.FETCH_MESSAGES_SUCCESS: {
      const payload = action.payload;
      draft.folders.byId[payload.folder] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case messageActions.FETCH_MESSAGES_APPEND: {
      const payload = action.payload;
      draft.folders.byId[payload.folder] = {
        ...draft.folders.byId[payload.folder],
        appendMessage: ENUMS.FETCHING
      };
      break;
    }

    case messageActions.FETCH_MESSAGES_APPEND_SUCCESS: {
      const payload = action.payload;
      draft.folders.byId[payload.folder] = {
        ...draft.folders.byId[payload.folder],
        appendMessage: ENUMS.SUCCESS
      };
      break;
    }

    case messageActions.FETCH_MESSAGES_APPEND_FAILED: {
      const payload = action.payload;
      draft.folders.byId[payload.folder] = {
        ...draft.folders.byId[payload.folder],
        appendMessage: ENUMS.FAILED
      };
      break;
    }

    case folderActions.FETCH_FOLDERS: {
      const payload = action.payload;
      const parentFolderId = payload.parentFolder?.id || null;
      const status = payload.silent ? ENUMS.SILENT : ENUMS.FETCHING;

      if (!parentFolderId) {
        draft.root.byId.folders = {
          message: status
        };
      } else {
        draft.subFolders.byId[parentFolderId] = {
          message: status
        };
      }

      break;
    }

    case folderActions.FETCH_FOLDERS_FAILED: {
      const payload = action.payload;
      const parentFolderId = payload.parentFolder?.id || null;

      if (!parentFolderId) {
        draft.root.byId.folders = {
          message: ENUMS.FAILED,
          errorText: payload.errorText
        };
      } else {
        draft.subFolders.byId[parentFolderId] = {
          message: ENUMS.FAILED,
          errorText: payload.errorText
        };
      }

      break;
    }

    case folderActions.FETCH_FOLDERS_SUCCESS: {
      const payload = action.payload;
      const parentFolderId = payload.parentFolder?.id || null;

      if (!parentFolderId) {
        draft.root.byId.folders = {
          message: ENUMS.SUCCESS
        };
      } else {
        draft.subFolders.byId[parentFolderId] = {
          message: ENUMS.SUCCESS
        };
      }
      break;
    }

    case conversationActions.FETCH_CONVERSATION:
    case singleMessageActions.FETCH_SINGLE_MESSAGE: {
      const payload = action.payload;
      const status = payload.silent ? ENUMS.SILENT : ENUMS.FETCHING;

      draft.messages.byId[payload.id] = {
        message: status
      };
      break;
    }

    case conversationActions.FETCH_CONVERSATION_FAILED:
    case singleMessageActions.FETCH_SINGLE_MESSAGE_FAILED: {
      const payload = action.payload;
      draft.messages.byId[payload.id] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };
      break;
    }

    case conversationActions.FETCH_CONVERSATION_SUCCESS:
    case singleMessageActions.FETCH_SINGLE_MESSAGE_SUCCESS: {
      const payload = action.payload;
      draft.messages.byId[payload.id] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case logsActions.FETCH_LOGS: {
      const status = ENUMS.FETCHING;
      draft.logs.byId["list"] = {
        message: status
      };

      break;
    }

    case logsActions.FETCH_LOGS_FAILED: {
      const payload = action.payload;
      draft.logs.byId["list"] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };

      break;
    }

    case logsActions.FETCH_LOGS_SUCCESS: {
      draft.logs.byId["list"] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case activeDevicesActions.FETCH_ACTIVE_DEVICES: {
      const status = ENUMS.FETCHING;

      draft.activeDevices.byId["list"] = {
        message: status
      };

      break;
    }

    case activeDevicesActions.FETCH_ACTIVE_DEVICES_FAILED: {
      const payload = action.payload;
      draft.activeDevices.byId["list"] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };

      break;
    }

    case activeDevicesActions.FETCH_ACTIVE_DEVICES_SUCCESS: {
      draft.activeDevices.byId["list"] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case failedSessionsActions.FETCH_FAILED_SESSIONS: {
      const status = ENUMS.FETCHING;

      draft.failedSessions.byId["list"] = {
        message: status
      };

      break;
    }

    case failedSessionsActions.FETCH_FAILED_SESSIONS_FAILED: {
      const payload = action.payload;
      draft.failedSessions.byId["list"] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };

      break;
    }

    case failedSessionsActions.FETCH_FAILED_SESSIONS_SUCCESS: {
      draft.failedSessions.byId["list"] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case quarantinesActions.FETCH_QUARANTINES: {
      const status = ENUMS.FETCHING;

      draft.quarantines.byId["list"] = {
        message: status
      };

      break;
    }

    case quarantinesActions.FETCH_QUARANTINES_FAILED: {
      const payload = action.payload;
      draft.quarantines.byId["list"] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };

      break;
    }

    case quarantinesActions.FETCH_QUARANTINES_SUCCESS: {
      draft.quarantines.byId["list"] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case quarantinesActions.FETCH_QUARANTINES_APPEND: {
      draft.quarantines.byId["list"] = {
        ...draft.quarantines.byId["list"],
        appendQuarantine: ENUMS.FETCHING
      };
      break;
    }

    case quarantinesActions.FETCH_QUARANTINES_APPEND_SUCCESS: {
      draft.quarantines.byId["list"] = {
        ...draft.quarantines.byId["list"],
        appendQuarantine: ENUMS.SUCCESS
      };
      break;
    }

    case quarantinesActions.FETCH_QUARANTINES_APPEND_FAILED: {
      draft.quarantines.byId["list"] = {
        ...draft.quarantines.byId["list"],
        appendQuarantine: ENUMS.FAILED
      };
      break;
    }

    case singleQuarantineActions.FETCH_SINGLE_QUARANTINE: {
      const payload = action.payload;
      const status = payload.silent ? ENUMS.SILENT : ENUMS.FETCHING;

      draft.quarantines.byId[payload.id] = {
        message: status
      };
      break;
    }

    case singleQuarantineActions.FETCH_SINGLE_QUARANTINE_FAILED: {
      const payload = action.payload;
      draft.quarantines.byId[payload.id] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };
      break;
    }

    case singleQuarantineActions.FETCH_SINGLE_QUARANTINE_SUCCESS: {
      const payload = action.payload;
      draft.quarantines.byId[payload.id] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case archivesActions.FETCH_ARCHIVES: {
      const status = ENUMS.FETCHING;

      draft.archives.byId["list"] = {
        message: status
      };

      break;
    }

    case archivesActions.FETCH_ARCHIVES_FAILED: {
      const payload = action.payload;
      draft.archives.byId["list"] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };

      break;
    }

    case archivesActions.FETCH_ARCHIVES_SUCCESS: {
      draft.archives.byId["list"] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case archivesActions.FETCH_ARCHIVES_APPEND: {
      draft.archives.byId["list"] = {
        ...draft.archives.byId["list"],
        appendArchive: ENUMS.FETCHING
      };
      break;
    }

    case archivesActions.FETCH_ARCHIVES_APPEND_SUCCESS: {
      draft.archives.byId["list"] = {
        ...draft.archives.byId["list"],
        appendArchive: ENUMS.SUCCESS
      };
      break;
    }

    case archivesActions.FETCH_ARCHIVES_APPEND_FAILED: {
      const payload = action.payload;
      draft.archives.byId["list"] = {
        ...draft.archives.byId["list"],
        appendArchive: ENUMS.FAILED,
        errorText: payload.errorText
      };
      break;
    }

    case singleArchiveActions.FETCH_SINGLE_ARCHIVE: {
      const payload = action.payload;
      const status = payload.silent ? ENUMS.SILENT : ENUMS.FETCHING;

      draft.archives.byId[payload.id] = {
        message: status
      };
      break;
    }

    case singleArchiveActions.FETCH_SINGLE_ARCHIVE_FAILED: {
      const payload = action.payload;
      draft.archives.byId[payload.id] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };
      break;
    }

    case singleArchiveActions.FETCH_SINGLE_ARCHIVE_SUCCESS: {
      const payload = action.payload;
      draft.archives.byId[payload.id] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case savedArchiveSearchesActions.FETCH_SAVED_SEARCHES: {
      draft.savedArchiveSearches.byId["list"] = {
        message: ENUMS.FETCHING
      };
      break;
    }

    case savedArchiveSearchesActions.FETCH_SAVED_SEARCHES_FAILED: {
      const payload = action.payload;
      draft.savedArchiveSearches.byId["list"] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };
      break;
    }

    case savedArchiveSearchesActions.FETCH_SAVED_SEARCHES_SUCCESS: {
      draft.savedArchiveSearches.byId["list"] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case conversationActions.FETCH_SUB_CONVERSATIONS_MESSAGE: {
      const payload = action.payload;
      const status = payload.silent ? ENUMS.SILENT : ENUMS.FETCHING;

      draft.conversationsMessages.byId[payload.id] = {
        message: status
      };
      break;
    }

    case conversationActions.FETCH_SUB_CONVERSATIONS_MESSAGE_FAILED: {
      const payload = action.payload;
      draft.conversationsMessages.byId[payload.id] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };
      break;
    }

    case conversationActions.FETCH_SUB_CONVERSATIONS_MESSAGE_SUCCESS: {
      const payload = action.payload;
      draft.conversationsMessages.byId[payload.id] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case conversationActions.FETCH_SUB_CONVERSATIONS: {
      const payload = action.payload;
      const status = payload.silent ? ENUMS.SILENT : ENUMS.FETCHING;

      draft.conversations.byId[payload.id] = {
        message: status
      };
      break;
    }

    case conversationActions.FETCH_SUB_CONVERSATIONS_FAILED: {
      const payload = action.payload;
      draft.conversations.byId[payload.id] = {
        message: ENUMS.FAILED,
        errorText: payload.errorText
      };
      break;
    }

    case conversationActions.FETCH_SUB_CONVERSATIONS_SUCCESS: {
      const payload = action.payload;
      draft.conversations.byId[payload.id] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case rulesActions.IN_FETCHING: {
      const status = ENUMS.FETCHING;

      draft.rules.byId["in_list"] = {
        message: status
      };

      break;
    }

    case rulesActions.OUT_FETCHING: {
      const status = ENUMS.FETCHING;

      draft.rules.byId["out_list"] = {
        message: status
      };

      break;
    }

    case rulesActions.SET_IN_ERROR_MESSAGE: {
      const payload = action.payload;
      draft.rules.byId["in_list"] = {
        message: ENUMS.FAILED,
        errorText: payload
      };

      break;
    }

    case rulesActions.SET_OUT_ERROR_MESSAGE: {
      const payload = action.payload;
      draft.rules.byId["out_list"] = {
        message: ENUMS.FAILED,
        errorText: payload
      };

      break;
    }

    case rulesActions.FETCH_IN_SUCCESS: {
      draft.rules.byId["in_list"] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    case rulesActions.FETCH_OUT_SUCCESS: {
      draft.rules.byId["out_list"] = {
        message: ENUMS.SUCCESS
      };
      break;
    }

    default:
      return draft;
  }
}, initialState);

export default reducer;
